/**
 * Utility functions for generative UI components.
 */

import { analytics } from '@commander/product-analytics';
import type { BadgeProps } from '@commander/ui';

const SAFE_SCHEME_RE = /^(https?:|mailto:|tel:)/i;

/**
 * Validate an href against an allowlist of safe URI schemes.
 * Returns the href unchanged if safe, or null if it should not be rendered as a link.
 */
export function sanitizeHref(href: string | undefined | null): string | null {
  if (!href) return null;
  const trimmed = href.trim();
  if (trimmed.length === 0) return null;
  // Relative paths (internal app navigation)
  if (trimmed.startsWith('/')) return trimmed;
  // Absolute URLs with safe schemes
  if (SAFE_SCHEME_RE.test(trimmed)) return trimmed;
  // Everything else rejected
  return null;
}

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const currencyFormatterDecimals = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const numberFormatter = new Intl.NumberFormat('en-US');

const dateFormatter = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
});

export function formatValue(value: unknown, type?: string): string {
  if (value === null || value === undefined || value === '') return '--';

  switch (type) {
    case 'currency': {
      const num = Number(value);
      if (Number.isNaN(num)) return String(value);
      // Use decimals for values with fractional parts
      return num % 1 === 0 ? currencyFormatter.format(num) : currencyFormatterDecimals.format(num);
    }
    case 'percent': {
      const num = Number(value);
      if (Number.isNaN(num)) return String(value);
      // If value is between 0 and 1 (exclusive), treat as ratio
      const pct = num > 0 && num < 1 ? num * 100 : num;
      return `${pct.toFixed(1)}%`;
    }
    case 'date': {
      const str = String(value);
      const date = new Date(str);
      if (Number.isNaN(date.getTime())) return str;
      return dateFormatter.format(date);
    }
    case 'number': {
      const num = Number(value);
      if (Number.isNaN(num)) return String(value);
      return numberFormatter.format(num);
    }
    default:
      return String(value);
  }
}

/**
 * Replace {id} in a URL pattern with the actual ID from a row.
 * Returns null if the ID field is missing or nullish.
 */
export function resolveDeepLink(
  pattern: string,
  row: Record<string, unknown>,
  idField: string
): string | null {
  const id = row[idField];
  if (id === null || id === undefined || id === '') return null;
  return sanitizeHref(pattern.replace('{id}', String(id)));
}

type BadgeVariant = NonNullable<BadgeProps['variant']>;

// Map agent color names to distinct Badge color variants. Mirrors the
// heatmap StatusBadge / ViewSummaryPopover convention in the prospecting app
// (green=engaged, orange=targeting, gray=pending, red=hot) so each status
// reads as a distinct color.
const COLOR_TO_BADGE_VARIANT: Record<string, BadgeVariant> = {
  green: 'engaged',
  yellow: 'targeting',
  orange: 'targeting',
  red: 'hot',
  blue: 'customer',
  purple: 'nextStep',
  gray: 'pending',
};

/**
 * Map a status value to a shared Badge variant using an optional color map.
 */
// Fallback used when the agent didn't pass `status_colors`. Maps common
// prospect/deal status labels to the same distinct-color variants above.
const PROSPECT_STATUS_DEFAULT: Record<string, BadgeVariant> = {
  engaged: 'engaged',
  deal: 'engaged',
  customer: 'customer',
  targeting: 'targeting',
  recommended: 'nextStep',
  pending: 'pending',
  denied: 'hot',
  deleted: 'hot',
  archived: 'pending',
};

export function getStatusBadgeVariant(
  status: string,
  colorMap?: Record<string, string>
): BadgeVariant {
  const mapped = colorMap?.[status];
  if (mapped && COLOR_TO_BADGE_VARIANT[mapped]) return COLOR_TO_BADGE_VARIANT[mapped];
  const byLabel = PROSPECT_STATUS_DEFAULT[status.toLowerCase()];
  if (byLabel) return byLabel;
  return 'pending';
}

/**
 * Create a type-aware sort comparator for table rows.
 * Nullish values are always sorted to the end.
 */
/**
 * Format a number for KPI display with compact notation for large values.
 */
export function formatCompactValue(
  value: number,
  format?: 'number' | 'currency' | 'percent' | 'compact'
): string {
  switch (format) {
    case 'currency': {
      if (Math.abs(value) >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
      if (Math.abs(value) >= 1_000) return `$${(value / 1_000).toFixed(1)}K`;
      return currencyFormatter.format(value);
    }
    case 'percent': {
      const pct = value > 0 && value < 1 ? value * 100 : value;
      return `${pct.toFixed(1)}%`;
    }
    case 'compact': {
      if (Math.abs(value) >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
      if (Math.abs(value) >= 1_000) return `${(value / 1_000).toFixed(1)}K`;
      return numberFormatter.format(value);
    }
    default:
      return numberFormatter.format(value);
  }
}

/**
 * Map a progress color name to a static Tailwind class.
 * Dynamic classes like `bg-${color}-500` break Tailwind JIT tree-shaking.
 */
const PROGRESS_COLOR_MAP: Record<string, string> = {
  green: 'bg-green-500',
  red: 'bg-red-500',
  blue: 'bg-blue-500',
  yellow: 'bg-yellow-500',
  orange: 'bg-orange-500',
  purple: 'bg-purple-500',
  teal: 'bg-teal-500',
  gray: 'bg-gray-500',
};

export function getProgressColorClass(color?: string): string | undefined {
  if (!color) return undefined;
  return PROGRESS_COLOR_MAP[color];
}

/**
 * Fire a deep link click analytics event.
 * Called from generative UI components when a user clicks a deep link.
 */
export function trackDeepLinkClick(
  ctx: { threadId: string; appContext: string } | null,
  blockType: string,
  destination: string,
  entityType?: string
) {
  if (!ctx) return;
  analytics.track('commander_go_deep_link_clicked', {
    app_context: ctx.appContext,
    thread_id: ctx.threadId,
    block_type: blockType,
    destination,
    entity_type: entityType,
  });
}

export function createSortComparator(
  key: string,
  type?: string,
  direction: 'asc' | 'desc' = 'asc'
): (a: Record<string, unknown>, b: Record<string, unknown>) => number {
  const dir = direction === 'desc' ? -1 : 1;

  return (a, b) => {
    const av = a[key];
    const bv = b[key];

    // Nullish values to the end
    if (av == null && bv == null) return 0;
    if (av == null) return 1;
    if (bv == null) return -1;

    switch (type) {
      case 'number':
      case 'currency':
      case 'percent':
        return (Number(av) - Number(bv)) * dir;
      case 'date': {
        const ta = new Date(String(av)).getTime();
        const tb = new Date(String(bv)).getTime();
        if (Number.isNaN(ta) && Number.isNaN(tb)) return 0;
        if (Number.isNaN(ta)) return 1;
        if (Number.isNaN(tb)) return -1;
        return (ta - tb) * dir;
      }
      default:
        return String(av).localeCompare(String(bv)) * dir;
    }
  };
}
